import { useState } from "react";
import { useLocalStorage } from "@/lib/hooks/useLocalStorage";
import useAppLinks from "@/lib/hooks/useAppLinks";
import useInit from "@/lib/queries/user/useInit";
import { TooltipProvider } from "@/components/ui/tooltip";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { CaretDown, CaretRight } from "@phosphor-icons/react";
import Title from "@/components/Title";
import ArticleTitle from "@/components/ArticleTitle";
import PilotBadge from "@/components/PilotBadge";
import { useStytchSession } from "@stytch/react";
import useUser from "@/lib/queries/user/useUser";
import PromotionalSection from "@/components/PromotionalSection";
import PromotionalSectionSkeleton from "@/components/PromotionalSectionSkeleton";
import SectionHeader from "@/components/SectionHeader";
import ToolsSection from "@/components/ToolsSection";
import ToolsGridSkeleton from "@/components/ToolsGridSkeleton";
import Footer from "@/components/Footer";
import { useTranslation } from "react-i18next";

function LandingPage() {
  const { t } = useTranslation("landingPage");
  const { tools: appLinks, isPilot } = useAppLinks();
  const { isLoading: isInitLoading, isPlaceholderData: isInitPlaceholder } =
    useInit();
  const [bookmarks, setBookmarks] = useLocalStorage<string[]>("bookmarks", []);
  const [isComingSoonOpen, setIsComingSoonOpen] = useState(false);
  const { session } = useStytchSession();
  const { data: user, isLoading: isUserLoading } = useUser({
    enabled: session !== null,
  });

  // Determine if we should show loading states
  const isToolsLoading = isInitLoading || isInitPlaceholder;
  const isPromotionalLoading = session ? isUserLoading : false;

  const toggleBookmark = (id: string) => {
    if (bookmarks.includes(id)) {
      setBookmarks(bookmarks.filter((bookmarkId) => bookmarkId !== id));
    } else {
      setBookmarks([...bookmarks, id]);
    }
  };

  return (
    <>
      <TooltipProvider>
        <main
          className="flex flex-col pt-[30px] md:pt-[24px] bg-very-light w-full flex-1"
          aria-label={t("main.ariaLabel")}
          aria-description={t("main.ariaDescription")}
        >
          <div className="flex flex-wrap items-center gap-4 px-4 md:px-10">
            <Title title={t("header.title")}>{t("header.title")}</Title>
            {isPilot && <PilotBadge />}
          </div>
          <p
            className="inline-block px-4 overflow-visible text-transparent bg-clip-text bg-gradient-to-r text-clip md:px-10"
            style={{
              backgroundImage: `linear-gradient(to right, hsl(var(--blue-gradient)), hsl(var(--primary)), hsl(var(--primary)))`,
            }}
          >
            {t("header.tagline")}
          </p>

          <section
            className="flex flex-col gap-[76px] px-4 md:px-10"
            aria-label={t("introduction.sectionAriaLabel")}
          >
            <div className="flex flex-col md:flex-row gap-[43px] mt-[43px]">
              <article className="flex flex-col gap-[25px]   max-w-[600px]">
                <ArticleTitle>{t("introduction.aboutUs.title")}</ArticleTitle>
                <p className="text-small-content text-dark-grey">
                  {t("introduction.aboutUs.description")}
                </p>
              </article>
              <article className="flex flex-col gap-[25px]   max-w-[600px]">
                <ArticleTitle>
                  {t("introduction.howItWorks.title")}
                </ArticleTitle>
                <p className="text-small-content text-dark-grey">
                  {t("introduction.howItWorks.description")}
                </p>
              </article>
            </div>
          </section>
          {!session && (
            <PromotionalSection
              title={t("promotional.guestUser.title")}
              description={t("promotional.guestUser.description")}
              primaryButtonText={t("promotional.guestUser.primaryButtonText")}
              primaryButtonLink="/authenticate"
              secondaryButtonText={t(
                "promotional.guestUser.secondaryButtonText"
              )}
              secondaryButtonLink="/pricing"
            />
          )}
          {session && isPromotionalLoading && <PromotionalSectionSkeleton />}
          {session && !isPromotionalLoading && !user?.subscribed && (
            <PromotionalSection
              title={t("promotional.unsubscribedUser.title")}
              description={t("promotional.unsubscribedUser.description")}
              primaryButtonText={t(
                "promotional.unsubscribedUser.primaryButtonText"
              )}
              primaryButtonLink="/checkout"
              secondaryButtonText={t(
                "promotional.unsubscribedUser.secondaryButtonText"
              )}
              secondaryButtonLink="/pricing"
              variant="default"
            />
          )}
          {/* Show favourites section only if user has bookmarks */}
          {bookmarks.length > 0 && (
            <section
              className="max-w-full mt-[43px] px-4 md:px-10"
              id="bookmarks"
              aria-label={t("bookmarks.ariaLabel")}
              aria-description={t("bookmarks.ariaDescription")}
            >
              <SectionHeader>{t("bookmarks.sectionHeader")}</SectionHeader>
              {isToolsLoading ? (
                <ToolsGridSkeleton count={2} />
              ) : (
                <ToolsSection
                  tools={appLinks}
                  bookmarks={bookmarks}
                  toggleBookmark={toggleBookmark}
                  userIsPilot={isPilot ?? false}
                  filter={(tool) =>
                    Boolean(tool?.id && bookmarks.includes(tool.id))
                  }
                  emptyStateTitle={t("bookmarks.emptyState.title")}
                  emptyStateDescription={t("bookmarks.emptyState.description")}
                />
              )}
            </section>
          )}
          {/* Tools section: "All tools" if no favourites, "Your other tools" if has favourites */}
          <section
            className="max-w-full px-4 md:px-10 mt-6"
            id="otherTools"
          >
            <SectionHeader>
              {bookmarks.length > 0
                ? t("tools.sectionHeader")
                : t("tools.allToolsSectionHeader")}
            </SectionHeader>
            {isToolsLoading ? (
              <ToolsGridSkeleton count={4} />
            ) : (
              <>
                <ToolsSection
                  tools={appLinks}
                  bookmarks={bookmarks}
                  toggleBookmark={toggleBookmark}
                  userIsPilot={isPilot ?? false}
                  filter={(tool) =>
                    Boolean(
                      tool?.id &&
                        (bookmarks.length === 0 || !bookmarks.includes(tool.id)) &&
                        !tool.comingSoon
                    )
                  }
                  emptyStateTitle={t("tools.allBookmarked.emptyStateTitle")}
                  emptyStateDescription={t(
                    "tools.allBookmarked.emptyStateDescription"
                  )}
                />
                {appLinks.filter(
                  (feature) =>
                    feature?.id &&
                    !bookmarks.includes(feature.id) &&
                    feature.comingSoon
                ).length > 0 && (
                  <Collapsible
                    open={isComingSoonOpen}
                    onOpenChange={setIsComingSoonOpen}
                    className="mb-8"
                  >
                    <CollapsibleTrigger asChild>
                      <Button
                        variant="ghost"
                        className="flex items-center justify-start h-auto gap-2 p-0 mt-10 text-lg font-semibold hover:bg-transparent"
                      >
                        {isComingSoonOpen ? (
                          <CaretDown className="w-4 h-4" />
                        ) : (
                          <CaretRight className="w-4 h-4" />
                        )}
                        {t("tools.comingSoon.triggerText")}
                      </Button>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="mt-4">
                      <ToolsSection
                        tools={appLinks}
                        bookmarks={bookmarks}
                        toggleBookmark={toggleBookmark}
                        userIsPilot={isPilot ?? false}
                        filter={(tool) =>
                          Boolean(
                            tool?.id &&
                              !bookmarks.includes(tool.id) &&
                              tool.comingSoon
                          )
                        }
                        emptyStateTitle={t("tools.comingSoon.emptyStateTitle")}
                        emptyStateDescription={t(
                          "tools.comingSoon.emptyStateDescription"
                        )}
                      />
                    </CollapsibleContent>
                  </Collapsible>
                )}
              </>
            )}
          </section>
          <Footer />
        </main>
      </TooltipProvider>
    </>
  );
}

export default LandingPage;
